import {useMemo, useState} from "react";
import {SiteSiteAssocEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseSiteSiteAssocDataTableFilterProps = Omit<UseDataTableFilterProps<SiteSiteAssocEntity>, 'filterConfig'>

export function useSiteSiteAssocDataTableFilter(props: UseSiteSiteAssocDataTableFilterProps): UseDataTableFilterResult<SiteSiteAssocEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [sourceNameQuery, setSourceNameQuery] = useState<string | undefined>(undefined);
  const [sourceNameDebouncedQuery] = useDebouncedValue(sourceNameQuery, 200);

  const [destNameQuery, setDestNameQuery] = useState<string | undefined>(undefined);
  const [destNameDebouncedQuery] = useDebouncedValue(destNameQuery, 200);

  const filterConfig: DataTableFilterConfig<SiteSiteAssocEntity> = useMemo(() => {
    return {
      "sourceName": {
        setQuery: setSourceNameQuery,
        query: sourceNameQuery,
        debouncedQuery: sourceNameDebouncedQuery,
        filter: (record, query) => !record.sourceSiteName || !record.sourceSiteName.toLowerCase().includes(query.toLowerCase())
      },
      "destName": {
        setQuery: setDestNameQuery,
        query: destNameQuery,
        debouncedQuery: destNameDebouncedQuery,
        filter: (record, query) => !record.destSiteName || !record.destSiteName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    sourceNameQuery,
    sourceNameDebouncedQuery,
    destNameQuery,
    destNameDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
